import type { OpeningSlot } from '@growup/arena-shared';

export const WEEKDAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

export const dayName = (weekday: number): string => WEEKDAYS[weekday - 1] ?? '';

/** 1 = Monday ... 7 = Sunday, as in OpeningSlot.weekday. */
export const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];
export const WORK_DAYS = [1, 2, 3, 4, 5];
export const WEEKEND = [6, 7];

export type Hours = Pick<OpeningSlot, 'opens' | 'closes'>;

export const DEFAULT_HOURS: Hours = { opens: '15:00', closes: '19:00' };

/** The slots of one day, in the order they were entered (sorting mid-edit would move rows). */
export function slotsOf(slots: OpeningSlot[], weekday: number): Hours[] {
  return slots.filter((s) => s.weekday === weekday).map(({ opens, closes }) => ({ opens, closes }));
}

/** Replaces the hours of each given day with `hours` (an empty list closes them). */
export function setDays(slots: OpeningSlot[], days: number[], hours: Hours[]): OpeningSlot[] {
  const kept = slots.filter((s) => !days.includes(s.weekday));
  const added = days.flatMap((weekday) => hours.map((h) => ({ weekday, ...h })));
  return [...kept, ...added];
}

export function sortSlots(slots: OpeningSlot[]): OpeningSlot[] {
  return [...slots].sort((a, b) => a.weekday - b.weekday || a.opens.localeCompare(b.opens));
}

/** A second slot for the day, starting when the first one closes (lunch break, evening shift). */
export function nextSlot(first: Hours): Hours {
  const [h = 0, m = 0] = first.closes.split(':').map(Number);
  const start = h * 60 + m;
  const end = Math.min(start + 120, 23 * 60 + 59);
  const fmt = (n: number) =>
    `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
  return { opens: first.closes, closes: fmt(end) };
}

/** Days whose slots overlap each other, as messages for the form. */
export function overlapErrors(slots: OpeningSlot[]): string[] {
  return ALL_DAYS.flatMap((day) => {
    const daySlots = slotsOf(slots, day).sort((a, b) => a.opens.localeCompare(b.opens));
    const overlaps = daySlots.some((s, i) => i > 0 && s.opens < (daySlots[i - 1]?.closes ?? ''));
    return overlaps ? [`Os horários de ${dayName(day)} sobrepõem-se.`] : [];
  });
}
